import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Star, Zap } from "lucide-react";
import Sparkles from "./Sparkles";
import Diya from "./Diya";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = ({ onShopNow }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bloomY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden bg-[#0B0F19] pt-28"
      data-testid="hero-section"
    >
      {/* Atmospheric golden light blooms */}
      <motion.div style={{ y: bloomY }} className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute -top-20 right-[8%] h-[620px] w-[620px] bloom-gold" />
        <div className="absolute bottom-[-10%] left-[-6%] h-[560px] w-[560px] bloom-indigo" />
        <div className="absolute right-[18%] top-1/3 h-[420px] w-[420px] bloom-champagne opacity-70" />
        <div className="absolute bottom-1/4 left-1/3 h-[300px] w-[300px] bloom-crimson opacity-50" />
      </motion.div>

      {/* Vignette */}
      <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-[#0B0F19] via-transparent to-[#0B0F19]/60" />

      {/* Golden festive dust */}
      <Sparkles count={30} className="z-[2]" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 py-16 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease }}
          className="mb-4 font-serif text-2xl italic tracking-tight text-[#D4AF37] md:text-3xl"
          lang="hi"
          data-testid="hero-hindi-greeting"
        >
          ✦ शुभ दीपावली ✦
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease }}
          className="mb-8 inline-flex items-center gap-2 rounded-full glass px-4 py-2"
          data-testid="hero-trust-badge"
        >
          <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={2} />
          <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#E8E2D0]">
            Powered by Consortium Gifts · 26 Years of Trust
          </span>
        </motion.div>

        <h1 className="max-w-4xl font-serif text-[3.2rem] font-medium leading-[1.0] tracking-tight text-[#F3EEE3] text-glow-gold sm:text-6xl md:text-7xl lg:text-[6.5rem]">
          <Line delay={0.35}>Aapki Diwali,</Line>
          <Line delay={0.55}>
            <span className="italic text-[#D4AF37]">Hamari Zimmedari</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease }}
          className="mt-8 max-w-xl font-sans text-base leading-relaxed text-[#A9B0C2] md:text-lg"
        >
          Express luxury Diwali gifting, delivered in 2 days. Curated, fulfilled and
          trusted by Consortium Gifts — with 26 years of excellence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1, ease }}
          className="mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center"
        >
          <button
            onClick={onShopNow}
            data-testid="shop-now-hero-btn"
            className="btn-gold group flex items-center gap-3 rounded-full px-9 py-4 font-sans text-sm font-semibold tracking-wide"
          >
            Explore Express Collections
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
          </button>

          <div className="flex items-center gap-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-[#D4AF37]" fill="#D4AF37" strokeWidth={0} />
              ))}
            </div>
            <span className="font-sans text-sm text-[#8B93A7]">Rated 4.9 by 12,000+ patrons</span>
          </div>
        </motion.div>

        {/* Elegant glowing diya row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 flex items-end gap-9 sm:gap-14"
          data-testid="hero-diya-row"
        >
          <Diya size={52} delay={0} />
          <Diya size={66} delay={0.6} />
          <Diya size={52} delay={1.1} />
          <div className="hidden sm:block">
            <Diya size={60} delay={0.3} />
          </div>
        </motion.div>
      </motion.div>

      {/* Floating express delivery badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 1.3, ease }}
        className="absolute right-6 top-1/3 z-10 hidden lg:block"
        data-testid="hero-floating-badge"
      >
        <div className="glass flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-3" style={{ animation: "gentle-float 4s ease-in-out infinite" }}>
          <Zap className="h-4 w-4 text-[#D4AF37]" fill="#D4AF37" strokeWidth={0} />
          <span className="font-sans text-sm font-medium tracking-wide text-[#F3E5AB]">Express 48-Hour Delivery</span>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex h-10 w-6 items-start justify-center rounded-full border border-[#F3E5AB]/20 p-1.5">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
